import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface EditSponsorshipDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sponsorship: any;
  ptaId: string;
  userId: string;
  onSuccess: () => void;
}

export function EditSponsorshipDialog({
  open,
  onOpenChange,
  sponsorship,
  ptaId,
  userId,
  onSuccess,
}: EditSponsorshipDialogProps) {
  const [formData, setFormData] = useState({
    name: "",
    amount: "",
    purpose: "",
    sponsored_date: new Date().toISOString().split('T')[0],
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (sponsorship) {
      setFormData({
        name: sponsorship.name || "",
        amount: sponsorship.amount?.toString() || "",
        purpose: sponsorship.purpose || "",
        sponsored_date: sponsorship.sponsored_date || new Date().toISOString().split('T')[0],
      });
    } else {
      setFormData({
        name: "",
        amount: "",
        purpose: "",
        sponsored_date: new Date().toISOString().split('T')[0],
      });
    }
  }, [sponsorship]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (sponsorship) {
        const { error } = await supabase
          .from("business_sponsorships")
          .update({
            name: formData.name,
            amount: parseFloat(formData.amount),
            purpose: formData.purpose,
            sponsored_date: formData.sponsored_date,
          })
          .eq("id", sponsorship.id);

        if (error) throw error;
        toast.success("Sponsorship updated successfully");
      } else {
        const { error } = await supabase
          .from("business_sponsorships")
          .insert({
            pta_id: ptaId,
            name: formData.name,
            amount: parseFloat(formData.amount),
            purpose: formData.purpose,
            sponsored_date: formData.sponsored_date,
            created_by: userId,
          });

        if (error) throw error;
        toast.success("Sponsorship added successfully");
      }

      onSuccess();
      onOpenChange(false);
    } catch (error) {
      console.error("Error saving sponsorship:", error);
      toast.error("Failed to save sponsorship");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{sponsorship ? "Edit Sponsorship" : "Add Sponsorship"}</DialogTitle>
          <DialogDescription>
            {sponsorship ? "Update sponsorship details" : "Record a new business sponsorship"}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Business/Person Name</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="amount">Amount (£)</Label>
            <Input
              id="amount"
              type="number"
              step="0.01"
              min="0"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="purpose">Purpose</Label>
            <Textarea
              id="purpose"
              value={formData.purpose}
              onChange={(e) => setFormData({ ...formData, purpose: e.target.value })}
              rows={3}
              placeholder="e.g., Summer Fair sponsorship, Equipment donation..."
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="sponsored_date">Date</Label>
            <Input
              id="sponsored_date"
              type="date"
              value={formData.sponsored_date}
              onChange={(e) => setFormData({ ...formData, sponsored_date: e.target.value })}
              required
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? "Saving..." : "Save"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
